import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
  type ComponentType,
  type SVGProps,
} from "react";
import {
  Link,
  NavLink,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router";
import { AuthGuard } from "@app/components/auth-guard/auth-guard";
import { PermissionGuard } from "@app/components/auth-guard/permission-guard";
import { Footer } from "@app/components/footer/footer";
import { formatOneworld } from "@app/components/oneworld-word";
import { ReleaseUpdateBanner } from "@app/components/release-update-banner";
import { AuthModal } from "@app/components/templates/auth-modal";
import { useReleaseUpdate } from "@app/hooks/use-release-update";
import {
  AUTH_STATUS,
  SHELL_VARIANTS,
  type ShellVariant,
} from "@app/lib/auth-constants";
import { isDemoMode } from "@app/lib/app-mode";
import { getDefaultPathForRole } from "@app/lib/auth/navigation";
import { EventsAdminDemoProvider } from "@app/lib/events-admin-demo-context";
import {
  MaterialGridViewIcon,
  MaterialLogoutIcon,
  TrendingIcon,
  MaterialAccountCircleIcon,
} from "@app/lib/events-admin-icons";
import {
  type AppRouteDefinition,
  appRouteConfig,
} from "@app/lib/routes/app-route-config";
import { ONEWORLD_LOGO_SRC } from "@app/lib/oneworld-branding";
import { AgreementReviewPage } from "@app/pages/agreement-review/agreement-review-page";
import { ROLE_LABELS } from "@app/lib/role-constants";
import { getRoleLabelFromJwtRoles } from "@app/lib/role-utils";
import { useNotifications } from "@app/lib/ui-foundation";
import { useJwtAuth } from "@app/providers/jwt-auth-provider";
import "./App.css";

type NavItem = {
  to: string;
  label: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  isDisabled?: boolean;
};

const SHELL_LABEL_BY_VARIANT: Record<ShellVariant, string | null> = {
  [SHELL_VARIANTS.ADMIN]: "ONEWORLD ADMIN SECTION",
  [SHELL_VARIANTS.GUEST]: null,
  [SHELL_VARIANTS.LOADING]: null,
};

const AUTH_ROUTE_PREFIXES = ["/login", "/signup"];
function routeElementWithPolicy(route: AppRouteDefinition) {
  if (route.policy === "public") {
    return route.element;
  }

  const protectedElement = route.requiredPermission ? (
    <PermissionGuard action={route.requiredPermission}>
      {route.element}
    </PermissionGuard>
  ) : (
    route.element
  );

  return (
    <AuthGuard allowedRoles={route.allowedRoles}>{protectedElement}</AuthGuard>
  );
}

function AppShell({
  isReleaseUpdateAvailable,
}: {
  isReleaseUpdateAvailable: boolean;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const { dismissAll } = useNotifications();
  const { status, isAuthenticated, logout, user } = useJwtAuth();
  const isProfileControlDisabled = true;

  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname, location.search, location.hash]);

  useEffect(() => {
    dismissAll();
  }, [dismissAll, location.pathname, location.search, location.hash]);

  const isOnAuthRoute =
    AUTH_ROUTE_PREFIXES.some((prefix) =>
      location.pathname.startsWith(prefix),
    ) || location.pathname.endsWith("/landing-page");

  const shellVariant: ShellVariant = (() => {
    if (status === AUTH_STATUS.LOADING) {
      return SHELL_VARIANTS.LOADING;
    }

    // JwtAuthProvider resolves errors to an unauthenticated state, so the
    // shell naturally recovers to the guest experience after loading.
    if (!isAuthenticated || isOnAuthRoute) {
      return SHELL_VARIANTS.GUEST;
    }

    return SHELL_VARIANTS.ADMIN;
  })();

  const shellVariantClass =
    shellVariant === SHELL_VARIANTS.LOADING
      ? SHELL_VARIANTS.GUEST
      : shellVariant;

  const shellLabel = SHELL_LABEL_BY_VARIANT[shellVariant];
  const roleLabel = getRoleLabelFromJwtRoles(user?.roles);
  const resolvedShellLabel =
    shellVariant === SHELL_VARIANTS.ADMIN && roleLabel === ROLE_LABELS.ORGANIZER
      ? null
      : shellLabel;

  const navItems: NavItem[] =
    shellVariant === SHELL_VARIANTS.GUEST ||
    shellVariant === SHELL_VARIANTS.LOADING
      ? []
      : roleLabel === ROLE_LABELS.ORGANIZER
        ? [
            {
              to: "/manage-events",
              label: "Manage Events",
              icon: MaterialGridViewIcon,
            },
          ]
        : roleLabel === ROLE_LABELS.ADMIN
          ? [
              {
                to: "/admin/dashboard",
                label: "Dashboard",
                icon: TrendingIcon,
              },
            ]
          : [];

  const displayName = user?.name || "Organizer";

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);

  const handleAuthSuccess = useCallback(() => {
    setIsAuthModalOpen(false);
    navigate(getDefaultPathForRole(roleLabel), { replace: true });
  }, [navigate, roleLabel]);

  return (
    <div className="events-app">
      <header className="events-app__header">
        <ReleaseUpdateBanner isVisible={isReleaseUpdateAvailable} />
        <div className={`events-shell events-shell--${shellVariantClass}`}>
          <div className="events-shell__brand">
            <Link
              to="/"
              className="events-shell__logo events-shell__logo--link"
              aria-label="oneworld home"
            >
              <img
                src={ONEWORLD_LOGO_SRC}
                alt="oneworld"
                className="events-shell__logo-image"
                decoding="async"
                referrerPolicy="no-referrer"
              />
            </Link>
            {resolvedShellLabel ? (
              <div className="events-shell__brand-copy">
                <span>{formatOneworld(resolvedShellLabel)}</span>
              </div>
            ) : null}
          </div>

          {navItems.length > 0 ? (
            <nav
              className={`events-shell__nav ${mobileMenuOpen ? "is-open" : ""}`}
              aria-label="Events admin navigation"
            >
              {navItems.map((item) =>
                item.isDisabled ? (
                  <span key={item.label} className="events-shell__nav-link">
                    <item.icon className="size-6" />
                    {item.label}
                  </span>
                ) : (
                  <NavLink
                    key={item.label}
                    to={item.to}
                    className={({ isActive }) =>
                      `events-shell__nav-link ${isActive ? "is-active" : ""}`
                    }
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <item.icon className="size-6" />
                    {item.label}
                  </NavLink>
                ),
              )}
            </nav>
          ) : (
            <div />
          )}

          <div className="events-shell__actions">
            {navItems.length > 0 && (
              <button
                type="button"
                className="events-shell__hamburger"
                aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
                aria-expanded={mobileMenuOpen}
                onClick={() => setMobileMenuOpen((prev) => !prev)}
              >
                <span
                  className={`events-shell__hamburger-icon ${mobileMenuOpen ? "is-open" : ""}`}
                />
              </button>
            )}
            {shellVariant === SHELL_VARIANTS.LOADING ? null : shellVariant ===
                SHELL_VARIANTS.GUEST && !isOnAuthRoute ? (
              <button
                type="button"
                className="events-shell__login-link"
                onClick={() => setIsAuthModalOpen(true)}
              >
                Login
              </button>
            ) : shellVariant !== SHELL_VARIANTS.GUEST ? (
              <>
                <div className="events-shell__user">
                  <span>{displayName}</span>
                  <small>{roleLabel}</small>
                </div>
                {roleLabel === ROLE_LABELS.ORGANIZER ? (
                  <span
                    className="events-shell__icon-button"
                    aria-label="Profile"
                    aria-disabled="true"
                    style={{ cursor: "default" }}
                  >
                    <MaterialAccountCircleIcon className="size-6" />
                  </span>
                ) : (
                  <button
                    type="button"
                    className="events-shell__icon-button"
                    aria-label="Profile"
                    onClick={
                      isProfileControlDisabled
                        ? undefined
                        : () => navigate("/settings")
                    }
                    disabled={isProfileControlDisabled}
                  >
                    <MaterialAccountCircleIcon className="size-6" />
                  </button>
                )}
                <button
                  type="button"
                  className="events-shell__icon-button events-shell__icon-button--ghost"
                  aria-label="Sign out"
                  onClick={() => {
                    logout();
                    navigate("/");
                  }}
                >
                  <MaterialLogoutIcon className="size-6" />
                </button>
              </>
            ) : null}
          </div>
        </div>
      </header>

      <main className="events-main">
        <Routes>
          {appRouteConfig.map((route) => (
            <Route
              key={route.path}
              path={route.path}
              element={routeElementWithPolicy(route)}
            />
          ))}
        </Routes>
      </main>

      <Footer />

      <AuthModal
        isOpen={isAuthModalOpen}
        onOpenChange={setIsAuthModalOpen}
        onSuccess={handleAuthSuccess}
      />
    </div>
  );
}

export function App() {
  const app = <AppRouter />;

  return isDemoMode() ? (
    <EventsAdminDemoProvider>{app}</EventsAdminDemoProvider>
  ) : (
    app
  );
}

function AppRouter() {
  const location = useLocation();
  const routeKey = `${location.pathname}${location.search}${location.hash}`;
  const isReleaseUpdateAvailable = useReleaseUpdate(routeKey);

  if (location.pathname === "/review-agreement") {
    return (
      <>
        <ReleaseUpdateBanner isVisible={isReleaseUpdateAvailable} />
        <AuthGuard allowedRoles={[ROLE_LABELS.ORGANIZER, ROLE_LABELS.ADMIN]}>
          <AgreementReviewPage />
        </AuthGuard>
      </>
    );
  }

  return <AppShell isReleaseUpdateAvailable={isReleaseUpdateAvailable} />;
}
